import { getActiveLocale } from '@/i18n/activeLocale';
import { DEFAULT_LOCALE, type Locale } from '@/i18n/config';
import {
  translations,
  type TranslationFileKey,
  type TranslationKey,
  type TranslationNamespace,
} from '@/i18n/translations';
import {
  type PluralForms,
  type TranslateOptions,
  type TranslationLeaf,
  type TranslationTree,
} from '@/i18n/types';

const INTERPOLATION_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;

function isPluralForms(leaf: TranslationLeaf | TranslationTree): leaf is PluralForms {
  return typeof leaf === 'object' && typeof (leaf as PluralForms).other === 'string';
}

function readLeaf(tree: TranslationTree, key: string): TranslationLeaf | null {
  let current: TranslationLeaf | TranslationTree | undefined = tree;

  for (const segment of key.split('.')) {
    if (typeof current !== 'object' || current === null) return null;
    current = (current as TranslationTree)[segment];
  }

  if (typeof current === 'string') return current;
  if (current && isPluralForms(current)) return current;
  return null;
}

function selectPluralCategory(locale: Locale, count: number): Intl.LDMLPluralRule {
  try {
    return new Intl.PluralRules(locale).select(count);
  } catch {
    return count === 1 ? 'one' : 'other';
  }
}

function resolvePluralForm(forms: PluralForms, locale: Locale, count: number | undefined): string {
  if (count === undefined) return forms.other;
  // Zero reads as the plural ("0 atletas"), never as `one`: pt-BR and fr put 0 in `one`, whose form
  // the catalogues often write with a literal 1 ("1 com acesso"). Fractions keep the CLDR rule.
  if (count === 0) return forms.zero ?? forms.other;

  return forms[selectPluralCategory(locale, count)] ?? forms.other;
}

function interpolate(template: string, options: TranslateOptions | undefined): string {
  if (!options) return template;

  return template.replace(INTERPOLATION_PATTERN, (match, name: string) => {
    const value = options[name];
    return value === undefined ? match : String(value);
  });
}

function translateWithFallback(
  locale: Locale,
  key: TranslationKey,
  options: TranslateOptions | undefined,
): string | null {
  const leaf = readLeaf(translations[locale], key);
  if (leaf === null) return null;

  const template =
    typeof leaf === 'string' ? leaf : resolvePluralForm(leaf, locale, options?.count);
  return interpolate(template, options);
}

/**
 * Resolves a key in the given locale, falling back to the default locale and
 * finally to the key itself so the UI never renders an empty string.
 */
export function translateWithLocale(
  locale: Locale,
  key: TranslationKey,
  options?: TranslateOptions,
): string {
  const translated = translateWithFallback(locale, key, options);
  if (translated !== null) return translated;

  if (locale !== DEFAULT_LOCALE) {
    const fallback = translateWithFallback(DEFAULT_LOCALE, key, options);
    if (fallback !== null) return fallback;
  }

  if (import.meta.env.DEV) console.warn(`[i18n] Missing translation for "${key}" (${locale})`);
  return key;
}

/**
 * Translates using the active locale. Prefer `useTranslate` inside components
 * so they re-render when the locale changes.
 */
export function translate(key: TranslationKey, options?: TranslateOptions): string {
  return translateWithLocale(getActiveLocale(), key, options);
}

/** Translates a local key in the catalogue that mirrors its source file. */
export function translateFile<Namespace extends TranslationNamespace>(
  namespace: Namespace,
  key: TranslationFileKey<Namespace>,
  options?: TranslateOptions,
): string {
  return translate(`${namespace}.${key}` as TranslationKey, options);
}

/**
 * Lazily translated lookup table.
 *
 * The web keeps many module-level label maps (`sportTitleMap`, `kindTitleMap`)
 * that are read as `map[key]` from dozens of files. Translating them eagerly
 * would freeze the copy at import time; turning each into a function would
 * ripple through every consumer. Each entry is defined as an enumerable getter
 * instead, so a read resolves with the locale active at that moment and
 * `Object.entries` keeps working.
 *
 * A component that only reads from the map does not subscribe to the i18n
 * context, so it picks the new language up on its next render rather than
 * immediately. Copy that must repaint on the switch belongs in `useTranslate`.
 */
export function translatedMap<Namespace extends TranslationNamespace, Key extends string>(
  namespace: Namespace,
  keys: Record<Key, TranslationFileKey<Namespace>>,
): Record<Key, string> {
  const map = {} as Record<Key, string>;

  for (const [mapKey, messageKey] of Object.entries(keys) as [
    Key,
    TranslationFileKey<Namespace>,
  ][]) {
    Object.defineProperty(map, mapKey, {
      enumerable: true,
      get: () => translateFile(namespace, messageKey),
    });
  }

  return map;
}
